import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import axios from 'axios'
import { VARIABLE_DEFINITIONS, resolveVariables } from '../utils/proposalVariables'

// Minimal fallback — the full defaults come from the server module-registry endpoint
const FALLBACK_REGISTRY = [
  { id: 'cover', defaults: {} },
  { id: 'intro', defaults: {} },
  { id: 'about', defaults: {} },
  { id: 'scope', defaults: {} },
  { id: 'startup', defaults: {} },
  { id: 'serviceArea', defaults: {} },
  { id: 'project', defaults: {} },
  { id: 'terms', defaults: {} }
]

export function useProposal (proposalId, url) {
  const [proposal, setProposal] = useState(null)
  const [template, setTemplate] = useState(null)
  const [moduleRegistry, setModuleRegistry] = useState(FALLBACK_REGISTRY)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [dirty, setDirty] = useState(false)
  const saveTimer = useRef(null)

  // Fetch module registry from server (for defaults)
  useEffect(() => {
    if (!url) return
    axios.get(`${url}/api/mtc-proposals/module-registry`)
      .then(res => { if (Array.isArray(res.data)) setModuleRegistry(res.data) })
      .catch(() => {})
  }, [url])

  // Fetch proposal from server
  const fetchProposal = useCallback(async () => {
    if (!proposalId || !url) return
    setLoading(true)
    try {
      const res = await axios.get(`${url}/api/mtc-proposals/${proposalId}`)
      setProposal(res.data)
      setTemplate(res.data.templateId || null)
      setError(null)
    } catch (err) {
      console.error('Failed to fetch proposal:', err)
      setError('Failed to load proposal')
    } finally {
      setLoading(false)
    }
  }, [proposalId, url])

  useEffect(() => {
    if (proposalId) fetchProposal()
  }, [proposalId, fetchProposal])

  // Save proposal to server (debounced)
  // Does NOT overwrite local state — local edits are the source of truth while editing.
  // Only the initial fetch and forceSave set proposal from server.
  const saveProposal = useCallback(async (data) => {
    if (!proposalId || !url) return
    setSaving(true)
    try {
      await axios.put(`${url}/api/mtc-proposals/${proposalId}`, data)
      setDirty(false)
      setError(null)
    } catch (err) {
      console.error('Failed to save proposal:', err)
      setError('Failed to save')
    } finally {
      setSaving(false)
    }
  }, [proposalId, url])

  const debouncedSave = useCallback((data) => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => saveProposal(data), 1500)
    setDirty(true)
  }, [saveProposal])

  // Update a module override
  const updateModuleOverride = useCallback((moduleId, fieldName, value) => {
    setProposal(prev => {
      if (!prev) return prev
      const newModules = prev.modules.map(m => {
        if (m.moduleId !== moduleId) return m
        return { ...m, overrides: { ...m.overrides, [fieldName]: value } }
      })
      const updated = { ...prev, modules: newModules }
      debouncedSave({ modules: newModules })
      return updated
    })
  }, [debouncedSave])

  // Bulk update multiple fields on a module at once (avoids race conditions)
  const updateModuleOverrides = useCallback((moduleId, fieldsObj) => {
    setProposal(prev => {
      if (!prev) return prev
      const newModules = prev.modules.map(m => {
        if (m.moduleId !== moduleId) return m
        return { ...m, overrides: { ...m.overrides, ...fieldsObj } }
      })
      const updated = { ...prev, modules: newModules }
      debouncedSave({ modules: newModules })
      return updated
    })
  }, [debouncedSave])

  // Toggle module enabled
  const toggleModule = useCallback((moduleId) => {
    setProposal(prev => {
      if (!prev) return prev
      const newModules = prev.modules.map(m => {
        if (m.moduleId !== moduleId) return m
        return { ...m, enabled: !m.enabled }
      })
      const updated = { ...prev, modules: newModules }
      debouncedSave({ modules: newModules })
      return updated
    })
  }, [debouncedSave])

  // Reorder modules
  const reorderModules = useCallback((fromIndex, toIndex) => {
    setProposal(prev => {
      if (!prev) return prev
      const newModules = [...prev.modules]
      const [moved] = newModules.splice(fromIndex, 1)
      newModules.splice(toIndex, 0, moved)
      const reordered = newModules.map((m, i) => ({ ...m, order: i }))
      const updated = { ...prev, modules: reordered }
      debouncedSave({ modules: reordered })
      return updated
    })
  }, [debouncedSave])

  // Update CRM snapshot (single field)
  const updateCrmSnapshot = useCallback((field, value) => {
    setProposal(prev => {
      if (!prev) return prev
      const newSnapshot = { ...prev.crmSnapshot, [field]: value }
      const updated = { ...prev, crmSnapshot: newSnapshot }
      debouncedSave({ crmSnapshot: newSnapshot })
      return updated
    })
  }, [debouncedSave])

  // Bulk update multiple CRM snapshot fields at once (avoids debounce race conditions)
  const updateCrmSnapshots = useCallback((fieldsObj) => {
    setProposal(prev => {
      if (!prev) return prev
      const newSnapshot = { ...prev.crmSnapshot, ...fieldsObj }
      const updated = { ...prev, crmSnapshot: newSnapshot }
      debouncedSave({ crmSnapshot: newSnapshot })
      return updated
    })
  }, [debouncedSave])

  // Update jobsiteId
  const updateJobsite = useCallback((jobsiteId) => {
    setProposal(prev => {
      if (!prev) return prev
      const updated = { ...prev, jobsiteId: jobsiteId || null }
      debouncedSave({ jobsiteId: jobsiteId || null })
      return updated
    })
  }, [debouncedSave])

  // Update status
  const updateStatus = useCallback((status) => {
    setProposal(prev => {
      if (!prev) return prev
      const updated = { ...prev, status }
      saveProposal({ status })
      return updated
    })
  }, [saveProposal])

  // Get merged data for a module (defaults + overrides)
  const getModuleData = useCallback((moduleId) => {
    const registry = moduleRegistry.find(m => m.id === moduleId)
    const mod = proposal?.modules?.find(m => m.moduleId === moduleId)
    return { ...(registry?.defaults || {}), ...(mod?.overrides || {}) }
  }, [proposal, moduleRegistry])

  // ── Variables ─────────────────────────────────────────────────

  const resolvedVariables = useMemo(() => {
    return resolveVariables(template, proposal)
  }, [template, proposal])

  const updateVariable = useCallback((key, value) => {
    setProposal(prev => {
      if (!prev) return prev
      const newVars = { ...(prev.variables || {}) }
      if (value === undefined) {
        delete newVars[key]
      } else {
        newVars[key] = value
      }
      const updated = { ...prev, variables: newVars }
      debouncedSave({ variables: newVars })
      return updated
    })
  }, [debouncedSave])

  const resetVariables = useCallback(() => {
    setProposal(prev => {
      if (!prev) return prev
      const updated = { ...prev, variables: {} }
      debouncedSave({ variables: {} })
      return updated
    })
  }, [debouncedSave])

  // Manual save (for save button)
  const forceSave = useCallback(() => {
    if (!proposal) return
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveProposal({
      modules: proposal.modules,
      crmSnapshot: proposal.crmSnapshot,
      status: proposal.status,
      variables: proposal.variables || {}
    })
  }, [proposal, saveProposal])

  return {
    proposal,
    template,
    loading,
    saving,
    dirty,
    error,
    updateModuleOverride,
    updateModuleOverrides,
    toggleModule,
    reorderModules,
    updateCrmSnapshot,
    updateCrmSnapshots,
    updateJobsite,
    updateStatus,
    getModuleData,
    forceSave,
    fetchProposal,
    moduleRegistry,
    // Variables
    variableDefinitions: VARIABLE_DEFINITIONS,
    resolvedVariables,
    updateVariable,
    resetVariables
  }
}

export default useProposal
